/*
 * TripArchivePanel — reusable, date-grouped archive with search + status filter.
 * Used by CompletedTripsPanel and CancelledTripsPanel.
 *
 * Search: rider name, phone, trip_id, broker trip_id, addresses.
 * Group: by pickup date (most-recent first).
 * Filter: from / to date range.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Calendar, Download, CheckCircle2, XCircle, User, MapPin } from "lucide-react";
import { apiClient } from "../../lib/api";

const TP = "/tpt";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}
function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
function dayKey(iso) {
  if (!iso) return "unknown";
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function TripArchivePanel({ status, title, subtitle, testidPrefix, icon: Icon }) {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [expanded, setExpanded] = useState({});
  const [refundTarget, setRefundTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Reuse the main trips endpoint; filter client-side by status.
      const { data } = await apiClient.get(`${TP}/trips`);
      const filtered = (data || []).filter((t) => (Array.isArray(status) ? status.includes(t.status) : t.status === status));
      // Sort most-recent first (by pickup, fallback to created_at)
      filtered.sort((a, b) => {
        const ax = a?.schedule?.requested_pickup || a?.created_at || "";
        const bx = b?.schedule?.requested_pickup || b?.created_at || "";
        return bx.localeCompare(ax);
      });
      setTrips(filtered);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load archive");
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const fromT = fromDate ? new Date(fromDate).getTime() : null;
    const toT = toDate ? new Date(toDate).getTime() + 86400000 : null; // inclusive

    return trips.filter((t) => {
      if (fromT || toT) {
        const at = new Date(t?.schedule?.requested_pickup || t?.created_at || 0).getTime();
        if (fromT && at < fromT) return false;
        if (toT && at >= toT) return false;
      }
      if (!needle) return true;
      const hay = [
        t.trip_id,
        t?.rider?.name,
        t?.rider?.phone,
        t?.broker?.broker_trip_id,
        t?.broker?.name,
        t?.pickup?.address,
        t?.dropoff?.address,
        t?.assigned_driver?.driver_name,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [trips, q, fromDate, toDate]);

  const grouped = useMemo(() => {
    const map = {};
    for (const t of filtered) {
      const k = dayKey(t?.schedule?.requested_pickup || t?.created_at);
      if (!map[k]) map[k] = [];
      map[k].push(t);
    }
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filtered]);

  const toggle = (k) => setExpanded((e) => ({ ...e, [k]: !e[k] }));

  const exportCsv = () => {
    if (!filtered.length) { toast.error("Nothing to export"); return; }
    const rows = [
      ["trip_id", "status", "rider_name", "rider_phone", "broker", "broker_trip_id", "pickup_at", "pickup_address", "dropoff_address", "driver", "duration_min", "distance_mi", "created_at"].join(","),
      ...filtered.map((t) => [
        t.trip_id,
        t.status,
        JSON.stringify(t?.rider?.name || ""),
        t?.rider?.phone || "",
        JSON.stringify(t?.broker?.name || ""),
        t?.broker?.broker_trip_id || "",
        t?.schedule?.requested_pickup || "",
        JSON.stringify(t?.pickup?.address || ""),
        JSON.stringify(t?.dropoff?.address || ""),
        JSON.stringify(t?.assigned_driver?.driver_name || ""),
        t?.schedule?.estimated_duration_min || "",
        t?.schedule?.estimated_distance_mi || "",
        t.created_at || "",
      ].join(","))
    ].join("\n");
    const blob = new Blob([rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${title.toLowerCase().replace(/\s+/g, "-")}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div data-testid={`${testidPrefix}-panel`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {Icon ? <Icon className="w-8 h-8 text-forest" /> : null}
          <div>
            <h2 className="text-xl font-bold text-slate_navy">{title}</h2>
            <p className="text-sm text-slate-500">{subtitle}</p>
          </div>
        </div>
        <button onClick={exportCsv} className="inline-flex items-center gap-1 bg-forest text-white px-3 py-2 rounded-md text-sm font-semibold" data-testid={`${testidPrefix}-export`}>
          <Download size={14}/> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E2E0D8] rounded-xl p-4 mb-6">
        <div className="grid md:grid-cols-3 gap-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Search</div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Name · phone · trip ID · address · driver…"
                data-testid={`${testidPrefix}-search`}
                className="w-full pl-9 border border-[#E2E0D8] rounded-md px-3 py-2 text-sm"
              />
            </div>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">From date</div>
            <div className="relative">
              <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-full pl-9 border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid={`${testidPrefix}-from`}/>
            </div>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">To date</div>
            <div className="relative">
              <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-full pl-9 border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" data-testid={`${testidPrefix}-to`}/>
            </div>
          </label>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
          <div><b>{filtered.length}</b> of {trips.length} shown</div>
          {(q || fromDate || toDate) && (
            <button onClick={() => { setQ(""); setFromDate(""); setToDate(""); }} className="underline">Clear filters</button>
          )}
        </div>
      </div>

      {/* Grouped list */}
      {loading && <div className="text-center text-slate-500 py-12" data-testid={`${testidPrefix}-loading`}>Loading archive…</div>}
      {!loading && grouped.length === 0 && (
        <div className="text-center py-16 text-slate-500 bg-white border border-dashed border-[#E2E0D8] rounded-xl" data-testid={`${testidPrefix}-empty`}>
          <div className="text-lg font-semibold mb-1">No trips match your search</div>
          <div className="text-sm">Adjust the date range or search terms above.</div>
        </div>
      )}
      {!loading && grouped.map(([day, dayTrips]) => (
        <div key={day} className="mb-4 bg-white border border-[#E2E0D8] rounded-xl overflow-hidden">
          <button
            onClick={() => toggle(day)}
            className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 border-b border-[#E2E0D8]"
            data-testid={`${testidPrefix}-day-${day}`}
          >
            <div className="flex items-center gap-2">
              <Calendar size={14} className="text-slate-500"/>
              <div className="font-semibold text-slate_navy text-sm">{fmtDate(dayTrips[0]?.schedule?.requested_pickup || dayTrips[0]?.created_at)}</div>
              <div className="text-xs text-slate-500">· {dayTrips.length} trip{dayTrips.length !== 1 ? "s" : ""}</div>
            </div>
            <div className="text-xs text-slate-400">{expanded[day] === false ? "Show" : "Hide"}</div>
          </button>
          {expanded[day] !== false && (
            <div className="divide-y divide-[#F1EFE8]">
              {dayTrips.map((t) => <TripRow key={t.trip_id} trip={t} testidPrefix={testidPrefix} openRefund={setRefundTarget} openForceCancel={setCancelTarget}/>)}
            </div>
          )}
        </div>
      ))}
      <ActionModals
        refundTarget={refundTarget}
        cancelTarget={cancelTarget}
        close={() => { setRefundTarget(null); setCancelTarget(null); }}
        onDone={load}
      />
    </div>
  );
}

function TripRow({ trip, testidPrefix, openRefund, openForceCancel }) {
  return (
    <div className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-slate-50 text-sm" data-testid={`${testidPrefix}-row-${trip.trip_id}`}>
      <div className="col-span-2 flex items-center gap-2">
        <User size={14} className="text-slate-400 shrink-0"/>
        <div className="min-w-0">
          <div className="font-semibold text-slate_navy truncate">{trip?.rider?.name || "—"}</div>
          <div className="text-[11px] text-slate-500">{trip?.rider?.mobility_type || "—"}</div>
        </div>
      </div>
      <div className="col-span-3 min-w-0">
        <div className="flex items-start gap-1">
          <MapPin size={11} className="mt-1 text-green-600 shrink-0"/>
          <span className="text-xs text-slate-700 truncate">{trip?.pickup?.address || "—"}</span>
        </div>
        <div className="flex items-start gap-1 mt-0.5">
          <MapPin size={11} className="mt-1 text-red-600 shrink-0"/>
          <span className="text-xs text-slate-700 truncate">{trip?.dropoff?.address || "—"}</span>
        </div>
      </div>
      <div className="col-span-2 text-xs">
        <div className="text-slate-500">{fmtTime(trip?.schedule?.requested_pickup)}</div>
        <div className="text-slate-400 mt-0.5">{trip?.broker?.name || "manual"}</div>
      </div>
      <div className="col-span-2 text-xs">
        <div className="font-semibold text-slate_navy">{trip?.assigned_driver?.driver_name || <span className="text-slate-400">unassigned</span>}</div>
        <div className="text-slate-400 mt-0.5">{trip?.assigned_tp?.tp_name || "—"}</div>
      </div>
      <div className="col-span-2 text-xs text-slate-500">
        <div>{trip?.schedule?.estimated_distance_mi ? `${trip.schedule.estimated_distance_mi} mi` : "—"}</div>
        <div>{trip?.schedule?.estimated_duration_min ? `${trip.schedule.estimated_duration_min} min` : "—"}</div>
      </div>
      <div className="col-span-1 text-right">
        {trip.status === "COMPLETED" && <CheckCircle2 className="w-5 h-5 text-green-600 inline"/>}
        {trip.status === "CANCELLED" && <XCircle className="w-5 h-5 text-red-500 inline"/>}
        {trip.status === "NO_SHOW_CONFIRMED" && <XCircle className="w-5 h-5 text-orange-500 inline" title="No-show"/>}
        <div className="text-[10px] text-slate-400 mt-1 font-mono truncate" title={trip.trip_id}>#{trip.trip_id.slice(0, 8)}</div>
        <div className="flex flex-col items-end gap-0.5 mt-1">
          <button
            onClick={() => downloadCompliancePacket(trip.trip_id)}
            data-testid={`${testidPrefix}-compliance-${trip.trip_id}`}
            className="text-[10px] text-forest hover:underline font-semibold"
            title="Compliance packet — for insurance, brokers, audits"
          >Proof ↓</button>
          {trip.payment_status === "captured" && (
            <button
              onClick={() => openRefund(trip)}
              data-testid={`${testidPrefix}-refund-${trip.trip_id}`}
              className="text-[10px] text-amber-700 hover:underline font-semibold"
              title="Refund captured payment"
            >Refund ↺</button>
          )}
          {!["COMPLETED","CANCELED","CANCELLED"].includes(trip.status) && (
            <button
              onClick={() => openForceCancel(trip)}
              data-testid={`${testidPrefix}-cancel-${trip.trip_id}`}
              className="text-[10px] text-red-600 hover:underline font-semibold"
              title="Force-cancel this trip"
            >Cancel ✕</button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Refund & Force-cancel action modals (admin-only) ---------- */
function ActionModals({ refundTarget, cancelTarget, close, onDone }) {
  const [amount, setAmount] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const target = refundTarget || cancelTarget;
  if (!target) return null;

  function reset() { setAmount(""); setReason(""); setBusy(false); }

  async function submitRefund() {
    if (!refundTarget) return;
    setBusy(true);
    try {
      const body = { reason: reason || "Admin refund" };
      if (amount) body.amount_usd = parseFloat(amount);
      await apiClient.post(`/admin/trips/${refundTarget.trip_id}/refund`, body);
      toast.success("Refund issued");
      reset(); close(); onDone?.();
    } catch (e) {
      const msg = e?.response?.data?.detail?.error?.message || e?.response?.data?.detail || e?.message || "Refund failed";
      toast.error(String(msg));
    } finally { setBusy(false); }
  }

  async function submitCancel() {
    if (!cancelTarget) return;
    if (!reason || reason.trim().length < 3) { toast.error("Please provide a reason (min 3 chars)."); return; }
    setBusy(true);
    try {
      await apiClient.post(`/admin/trips/${cancelTarget.trip_id}/force-cancel`, { reason: reason.trim(), release_hold: true });
      toast.success("Trip canceled");
      reset(); close(); onDone?.();
    } catch (e) {
      const msg = e?.response?.data?.detail?.error?.message || e?.response?.data?.detail || e?.message || "Cancel failed";
      toast.error(String(msg));
    } finally { setBusy(false); }
  }

  const isRefund = !!refundTarget;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
                   display: "flex", alignItems: "center", justifyContent: "center", zIndex: 70 }}
         onClick={() => !busy && close()}
         data-testid={isRefund ? "admin-refund-modal-backdrop" : "admin-cancel-modal-backdrop"}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 16, padding: 24, width: "min(440px, 92vw)",
                 border: "1px solid #E2E0D8", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
        data-testid={isRefund ? "admin-refund-modal" : "admin-cancel-modal"}>
        <div className="text-lg font-bold text-slate_navy mb-1">
          {isRefund ? "Refund this trip" : "Force-cancel this trip"}
        </div>
        <div className="text-xs text-slate-500 mb-4 font-mono">Trip #{target.trip_id.slice(0, 8)}</div>
        {isRefund && (
          <div className="mb-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Amount (USD) — blank = full refund</div>
            <input value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 12.50"
              data-testid="admin-refund-amount"
              className="w-full px-3 py-2 rounded-lg text-sm border border-[#E2E0D8]" />
          </div>
        )}
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Reason {isRefund ? "(optional)" : "(required)"}</div>
          <textarea value={reason} onChange={e => setReason(e.target.value)}
            placeholder={isRefund ? "e.g. Duplicate charge" : "e.g. Driver never arrived, rider requested cancel"}
            rows={3}
            data-testid={isRefund ? "admin-refund-reason" : "admin-cancel-reason"}
            className="w-full px-3 py-2 rounded-lg text-sm border border-[#E2E0D8]" />
        </div>
        <div className="flex gap-2">
          <button onClick={() => { reset(); close(); }} disabled={busy}
            className="flex-1 py-2 rounded-full font-semibold border border-slate-300" style={{ background: "#fff", color: "#334155" }}>Cancel</button>
          <button onClick={isRefund ? submitRefund : submitCancel} disabled={busy}
            data-testid={isRefund ? "admin-refund-submit" : "admin-cancel-submit"}
            className="flex-1 py-2 rounded-full font-bold text-white"
            style={{ background: isRefund ? "#B45309" : "#B91C1C", opacity: busy ? 0.6 : 1 }}>
            {busy ? "…" : (isRefund ? "Refund" : "Force-cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}

async function downloadCompliancePacket(tripId) {
  const toastId = toast.loading("Preparing compliance packet…");
  try {
    const { data } = await apiClient.get(`/admin/trips/${tripId}/compliance-packet`);
    const packet = data?.data || data;
    const blob = new Blob([JSON.stringify(packet, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `compliance-${tripId.slice(0, 8)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Compliance packet downloaded", { id: toastId });
  } catch (err) {
    const msg = err?.response?.data?.detail?.error?.message || err?.response?.data?.detail || err?.message || "Download failed";
    toast.error(String(msg), { id: toastId });
  }
}
